/* eslint-disable @typescript-eslint/no-explicit-any */
/** Provisional Project View — shown for projects still in Phase 1. Ported from `provisional-view.jsx`. */
import { useState } from 'react';
import { useData } from '../context/AppDataContext';
import { apiClient, ApiClientError } from '../lib/apiClient';
import { Ico, fmt, useToast } from '../components/ui';

export function ProvisionalProjectView({ projectId, backToList, onResumeBuilder }: { projectId: string; backToList: () => void; onResumeBuilder: (pid: string) => void }) {
  const D = useData();
  const toast = useToast();
  const p: any = D.projects.find((x) => x.id === projectId);
  const pm = D.team.find((u) => u.id === p.pi);

  // Real saved quotation versions for this project, newest first.
  const qs = D.quotations
    .filter((q) => q.projectId === p.id)
    .sort((a, b) => (a.number < b.number ? 1 : -1));

  const statusTone = (s?: string): string => (({ Draft: 'slate', Final: 'green', Archived: 'slate', 'Sent for consideration': 'amber', Accepted: 'green', Superseded: 'slate', Rejected: 'red' } as any)[s || 'Draft'] || 'slate');

  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const exportQuotationPdf = async (q: any) => {
    if (!q) return;
    setPdfBusyId(q.id);
    try {
      const a = q.adjustments || {};
      const adjustments = [
        { label: 'Discount', amount: -(a.discount || 0) },
        { label: 'Additional charge', amount: a.additional || 0 },
        { label: 'VAT 7%', amount: a.vat || 0 },
        { label: 'Rounding', amount: a.rounding || 0 },
      ].filter((x) => x.amount !== 0);
      const blob = await apiClient.quotations.pdf({
        studyCode: p.code,
        studyTitle: p.title,
        quotationNumber: String(q.number ?? ''),
        version: String(q.version ?? ''),
        dateText: String(q.submitted ?? ''),
        currency: 'THB',
        grandTotal: Math.round(q.grandTotal || 0),
        subtotal: Math.round((q.sections || []).reduce((s: number, sec: any) => s + (sec.total || 0), 0)),
        sections: (q.sections || []).map((s: any) => ({
          title: s.title,
          total: Math.round(s.total || 0),
          items: (s.items || []).filter((it: any) => it.included !== false).map((it: any) => ({
            code: it.code, name: it.name, qty: it.qty, unit: it.unit,
            price: Math.round(it.price || 0), total: Math.round(it.total || 0),
          })),
        })),
        adjustments,
      });
      const slug = String(q.number || p.code || 'quotation').replace(/[^a-z0-9-]+/gi, '_');
      const url = URL.createObjectURL(blob);
      const el = document.createElement('a');
      el.href = url; el.download = `Quotation_${slug}.pdf`; document.body.appendChild(el); el.click(); el.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      const m = err instanceof ApiClientError ? (err.body?.message ?? '') : String((err as any)?.message || '');
      toast(/not installed|Unavailable|503|pdflatex/i.test(m) ? 'Server LaTeX is unavailable — cannot generate the PDF.' : 'Quotation PDF failed — please try again.');
    } finally {
      setPdfBusyId(null);
    }
  };
  const latestForExport = qs.find((q) => q.status === 'Final') ?? qs[0];

  const phase1Steps = [
    { id: 'basics', label: 'Basics' },
    { id: 'design', label: 'Study Design' },
    { id: 'visits', label: 'Visit Schedule' },
    { id: 'labs', label: 'Labs & Procedures' },
    { id: 'variables', label: 'Variables' },
    { id: 'outcomes', label: 'Outcomes & Analysis' },
    { id: 'flow', label: 'Study Flow Diagram' },
    { id: 'review', label: 'Review Quotation' },
  ];
  const phase2Steps = [
    { id: 'accept', label: 'Acceptance & Agreement', state: 'awaiting' },
    { id: 'timeline', label: 'Timeline & Task allocation', state: 'locked' },
  ];

  return (
    <div className="page-pad fadein">
      <div style={{ marginBottom: 4 }}>
        <button className="btn ghost sm" onClick={backToList}><Ico.chevLeft /> Back to projects</button>
      </div>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.02em' }}>{p.code}</span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{p.shortTitle}</span>
            <span className="chip" data-tone="amber"><span className="dot" />Provisional · awaiting acceptance</span>
          </div>
          <h2 style={{ marginTop: 6, maxWidth: 980 }}>{p.title}</h2>
          <div className="sub" style={{ display: 'flex', gap: 14, marginTop: 6 }}>
            <span>{p.design}</span>
            <span>·</span>
            <span>{p.area}</span>
            <span>·</span>
            <span>Sponsor: {p.sponsor}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" disabled={!qs.length || !!pdfBusyId} onClick={() => void exportQuotationPdf(latestForExport)}><Ico.dl /> {pdfBusyId ? 'Generating…' : 'Export latest quotation'}</button>
          <button className="btn primary" onClick={() => onResumeBuilder(p.id)}>
            <Ico.arrowR /> Resume builder
          </button>
        </div>
      </div>

      <div style={{ padding: 16, marginBottom: 16, background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 22, fontWeight: 700, flexShrink: 0 }}>2</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>Awaiting sponsor &amp; PI acceptance</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
            Phase 1 (Scoping &amp; Quotation) saved on <span className="mono">{p.provisionalSavedAt}</span>. The active quotation <strong>{p.activeQuotationVersion}</strong> ({fmt.thb(p.activeQuotationTotal)}) is currently with the sponsor.
          </div>
        </div>
        <button className="btn primary" onClick={() => onResumeBuilder(p.id)}>Resume → Acceptance step</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-h"><h3>Builder progress</h3><span className="chip" data-tone="slate"><span className="dot" />Phase 1 of 2 complete</span></div>
            <div className="card-b" style={{ padding: 0 }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#4a8a55', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>✓</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phase 1 · Scoping &amp; Quotation</div>
                  <span className="chip" data-tone="green" style={{ marginLeft: 'auto' }}><span className="dot" />Complete</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, paddingLeft: 30 }}>
                  {phase1Steps.map((s, i) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 4, fontSize: 11.5 }}>
                      <span style={{ color: '#4a8a55', fontWeight: 700 }}>✓</span>
                      <span style={{ color: 'var(--text-2)' }}>{i + 1}. {s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>2</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phase 2 · Activation</div>
                  <span className="chip" data-tone="amber" style={{ marginLeft: 'auto' }}><span className="dot" />Awaiting sponsor/PI acceptance</span>
                </div>
                <div style={{ paddingLeft: 30, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {phase2Steps.map((s, i) => (
                    <div key={s.id} style={{ padding: 8, border: '1px solid ' + (s.state === 'awaiting' ? 'var(--accent)' : 'var(--border)'), borderRadius: 6, background: s.state === 'awaiting' ? 'var(--accent-soft)' : 'var(--surface-alt)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: s.state === 'awaiting' ? 'var(--accent)' : 'var(--text-3)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700 }}>{9 + i}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.label}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>
                          {s.state === 'awaiting' ? 'Open — sponsor signature, PM/PI confirmations needed' : 'Locked — unlocks after acceptance is recorded'}
                        </div>
                      </div>
                      {s.state === 'awaiting' && <button className="btn primary sm" onClick={() => onResumeBuilder(p.id)}>Open</button>}
                      {s.state === 'locked' && <Ico.cog />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <div>
                <h3>Quotation history</h3>
                <div className="sub">All saved versions for this provisional project</div>
              </div>
              <button className="btn primary sm" onClick={() => onResumeBuilder(p.id)}><Ico.edit /> Revise quotation</button>
            </div>
            <div className="card-b flush">
              <table>
                <thead><tr><th style={{ width: 40 }}>Active</th><th style={{ width: 70 }}>Version</th><th>Note</th><th className="num">Grand total</th><th>Status</th><th>Created</th><th></th></tr></thead>
                <tbody>
                  {qs.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>No quotation versions saved yet.</td></tr>
                  ) : qs.map((q) => (
                    <tr key={q.id} style={{ background: q.status === 'Final' ? 'rgba(74,138,85,0.05)' : undefined }}>
                      <td>{q.status === 'Final' ? <span className="chip" data-tone="green"><span className="dot" />✓</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                      <td className="mono" style={{ fontWeight: 600 }}>{q.version}</td>
                      <td style={{ fontSize: 12 }}>{q.adjustments?.reason || '—'}</td>
                      <td className="num tabular mono" style={{ fontWeight: 600 }}>{fmt.thb(q.grandTotal)}</td>
                      <td><span className="chip" data-tone={statusTone(q.status)}><span className="dot" />{q.status || 'Draft'}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text-2)' }}>
                        <div className="mono">{q.submitted}</div>
                      </td>
                      <td><button className="btn ghost icon sm" title="Export PDF" disabled={pdfBusyId === q.id} onClick={() => void exportQuotationPdf(q)}><Ico.dl /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card">
            <div className="card-h"><h3>Project basics</h3></div>
            <div className="card-b">
              <dl className="kv">
                <dt>Code</dt><dd className="mono">{p.code}</dd>
                <dt>Design</dt><dd>{p.design}</dd>
                <dt>Area</dt><dd>{p.area}</dd>
                <dt>Sponsor</dt><dd>{p.sponsor}</dd>
                <dt>Project Manager</dt><dd>{pm?.name}</dd>
                <dt>PI (doc)</dt><dd style={{ fontSize: 11 }}>{p.piName || '—'}</dd>
                <dt>Sample size</dt><dd className="tabular">{p.sampleSize}</dd>
                <dt>Start</dt><dd>{p.startDate}</dd>
                <dt>End</dt><dd>{p.endDate}</dd>
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>What happens next?</h3></div>
            <div className="card-b" style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.55, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>1. Sponsor reviews quotation <strong className="mono">{p.activeQuotationVersion}</strong> sent on 22 May.</div>
              <div>2. Once they sign off, the PM records acceptance in Phase 2 step 9.</div>
              <div>3. Phase 2 step 10 (Timeline &amp; Task allocation) unlocks.</div>
              <div>4. Clicking <strong>Activate project</strong> in the builder header turns this into a full active project — visible across the Monthly Dashboard, Team tabs, and Payments.</div>
              <hr className="rule" />
              <div>If the sponsor requests changes, click <strong>Resume builder</strong> → go back to any wizard step → save a new quotation version → re-send.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
